import {
  ApiError,
  backendFetchResponse,
} from "@softmax/observatory-client/transport";
type RawRead = {
  status: number;
  ok: boolean;
  body: unknown;
  headers: Headers;
};
function canonicalPath(path: string): string {
  const queryStart = path.indexOf("?");
  const route = queryStart === -1 ? path : path.slice(0, queryStart);
  const search = path.split("?", 2)[1];
  const parameters = new URLSearchParams(search);

  parameters.sort();
  return parameters.size ? `${route}?${parameters}` : route;
}
export function createPlayTransport({
  signal,
}: { signal?: AbortSignal | undefined } = {}) {
  const reads = new Map<string, Promise<RawRead>>();
  async function fetchRaw({
    path,
    publicRead,
  }: {
    path: string;
    publicRead: boolean;
  }): Promise<RawRead> {
    const response = await backendFetchResponse("/api/observatory", path, {
      headers: { "X-Use-Elevated-Privileges": "false" },
      credentials: publicRead ? "omit" : "same-origin",
      signal: signal ?? null,
    });
    return {
      status: response.status,
      ok: response.ok,
      body: response.ok ? await response.json() : null,
      headers: response.headers,
    };
  }
  function read({
    path,
    anonymous = false,
  }: {
    path: string;
    anonymous?: boolean;
  }): Promise<RawRead> {
    const canonical = canonicalPath(path);
    const key = `${anonymous ? "public" : "viewer"}:${canonical}`;
    let pending = reads.get(key);

    if (!pending) {
      pending = fetchRaw({
        path: canonical,
        publicRead: anonymous,
      });
      reads.set(key, pending);
    }

    return pending;
  }
  async function playFetchWithHeaders<T>({
    path,
    anonymous = false,
  }: {
    path: string;
    anonymous?: boolean;
  }): Promise<{
    body: T;
    headers: Headers;
  } | null> {
    const response = await read({
      path,
      anonymous: anonymous,
    });

    if (response.status === 401 || response.status === 403)
      throw new ApiError("This play resource requires access", response.status);

    if (!response.ok) {
      console.error(`[play] GET ${path} -> ${response.status}`);
      return null;
    }

    return { body: response.body as T, headers: response.headers };
  }
  const playFetch = async <T>({
    path,
    anonymous = false,
  }: {
    path: string;
    anonymous?: boolean;
  }): Promise<T | null> => {
    return (
      (
        await playFetchWithHeaders<T>({
          path,
          anonymous,
        })
      )?.body ?? null
    );
  };
  const get = async <T>({
    path,
    anonymous = false,
  }: {
    path: string;
    anonymous?: boolean;
  }): Promise<T> => {
    const response = await read({
      path,
      anonymous: anonymous,
    });

    if (!response.ok)
      throw new ApiError(
        `Play resource unavailable (${response.status})`,
        response.status,
      );

    return response.body as T;
  };
  return { playFetchWithHeaders, playFetch, get };
}

export type PlayTransport = ReturnType<typeof createPlayTransport>;
